import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'

const primaryBtn =
  'cursor-pointer flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40 disabled:scale-100'

const outlineBtn =
  'cursor-pointer flex w-full items-center justify-center rounded-full border border-border/60 bg-background py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'

// Floating footer card — sits over the scrolling content on a contained backdrop
// for legibility while the content still shows through around it. Only the card
// captures clicks. Shared by the plan preview and the onboarding steps.
// Pass offsetBottomNav when the mobile bottom nav is visible so the card clears it.
export function FloatingFooter({
  children,
  offsetBottomNav = false,
}: {
  children: ReactNode
  offsetBottomNav?: boolean
}) {
  return (
    <div
      className={
        offsetBottomNav
          ? 'pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4 pb-[calc(57px+env(safe-area-inset-bottom,0px)+0.75rem)] lg:pb-3'
          : 'pointer-events-none absolute inset-x-0 bottom-0 z-20 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)]'
      }
    >
      <div className="pointer-events-auto mx-auto flex max-w-lg flex-col gap-2 rounded-2xl border border-border/60 bg-background/90 p-3 shadow-lg backdrop-blur">
        {children}
      </div>
    </div>
  )
}

// Shared scaffold for reviewing an AI-generated plan (unsaved draft tree) — the
// header + tree scroll together, with an in-flow two-button footer that never
// overlaps the content. Used by the onboarding profile step and new-goal preview.
export function PlanReview({
  title,
  subtitle,
  headerTop,
  children,
  continueLabel,
  onContinue,
  continueLoading = false,
  startOverLabel,
  onStartOver,
  skipLabel,
  onSkip,
  footerTop,
  footerOverride,
  offsetBottomNav = false,
}: {
  title?: string
  subtitle?: string
  headerTop?: ReactNode
  children: ReactNode
  continueLabel: string
  onContinue: () => void
  continueLoading?: boolean
  startOverLabel?: string
  onStartOver?: () => void
  skipLabel?: string
  onSkip?: () => void
  footerTop?: ReactNode
  footerOverride?: ReactNode
  offsetBottomNav?: boolean
}) {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      {/* Header scrolls together with the tree; extra bottom padding so the last
          rows can scroll clear of the floating footer. The footerTop variant
          (new-goal preview) has a taller footer and needs more clearance. */}
      <div className="flex-1 overflow-y-auto">
        <div
          className={`mx-auto w-full max-w-3xl px-4 ${title ? 'pt-12' : 'pt-6'} ${footerTop ? 'pb-72 lg:pb-52' : 'pb-44 lg:pb-36'}`}
        >
          {headerTop}
          {title && <h1 className="text-2xl font-black tracking-tight">{title}</h1>}
          {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          <div className={title ? 'pt-6' : 'pt-2'}>{children}</div>
        </div>
      </div>

      {/* Floating footer — the CTAs sit on a contained backdrop for legibility
          while the tree still shows through around it; only the card captures
          clicks so the tree stays interactive */}
      {footerOverride ?? (
        <FloatingFooter offsetBottomNav={offsetBottomNav}>
          {footerTop}
          <button
            type="button"
            onClick={onContinue}
            disabled={continueLoading}
            className={primaryBtn}
          >
            {continueLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : continueLabel}
          </button>
          {onStartOver && (
            <button type="button" onClick={onStartOver} className={`${outlineBtn} hidden lg:flex`}>
              {startOverLabel}
            </button>
          )}
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="cursor-pointer pt-1 text-center text-sm text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            >
              {skipLabel}
            </button>
          )}
        </FloatingFooter>
      )}
    </div>
  )
}
